import { ensureFreshAccessToken } from '@/services/session';

/**
 * HTTP-клиент прикладного API: Bearer из сессии, один повтор после обновления токена.
 * Ошибки приводятся к кодам, которые UI уже понимает.
 */

export type ApiErrorCode = 'UNAUTHORIZED' | 'DENIED' | 'NOT_FOUND' | 'INVALID' | 'UNAVAILABLE' | 'INTERNAL';

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly requestId: string | null;

  constructor(code: ApiErrorCode, status: number, message?: string, requestId?: string | null) {
    super(message ?? code);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.requestId = requestId ?? null;
  }
}

interface ErrorBody {
  request_id?: string;
  error?: { type?: string; message?: string };
  detail?: string;
}

function mapStatus(status: number): ApiErrorCode {
  if (status === 401) return 'UNAUTHORIZED';
  if (status === 403) return 'DENIED';
  if (status === 404) return 'NOT_FOUND';
  if (status === 422 || status === 400) return 'INVALID';
  if (status >= 500) return 'INTERNAL';
  return 'INTERNAL';
}

async function readErrorBody(response: Response): Promise<ErrorBody> {
  try {
    return (await response.json()) as ErrorBody;
  } catch {
    return {};
  }
}

interface RequestOptions {
  method: string;
  body?: unknown;
  signal?: AbortSignal;
}

async function requestOnce(url: string, options: RequestOptions): Promise<Response> {
  const token = await ensureFreshAccessToken();

  if (!token) {
    throw new ApiError('UNAUTHORIZED', 401);
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
  };

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  try {
    return await fetch(url, {
      method: options.method,
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    throw new ApiError('UNAVAILABLE', 0);
  }
}

/** Принимает полный адрес: собрать его помогает `service.url(service.endpoints.x)`. */
async function request<T>(url: string, options: RequestOptions): Promise<T> {
  let response = await requestOnce(url, options);

  // Токен мог протухнуть между проверкой и запросом — обновляемся и пробуем ещё раз.
  if (response.status === 401) {
    const refreshed = await ensureFreshAccessToken();
    if (refreshed) {
      response = await requestOnce(url, options);
    }
  }

  if (!response.ok) {
    const body = await readErrorBody(response);
    throw new ApiError(
      mapStatus(response.status),
      response.status,
      body.error?.message ?? body.detail,
      body.request_id ?? response.headers.get('x-request-id'),
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export function apiGet<T>(url: string, signal?: AbortSignal): Promise<T> {
  return request<T>(url, { method: 'GET', signal });
}

export function apiPost<T>(url: string, body: unknown, signal?: AbortSignal): Promise<T> {
  return request<T>(url, { method: 'POST', body, signal });
}

export function apiPatch<T>(url: string, body: unknown, signal?: AbortSignal): Promise<T> {
  return request<T>(url, { method: 'PATCH', body, signal });
}

/** DELETE с телом: сервер ждёт `{ user_id }`, поэтому тело обязательно. */
export function apiDelete<T>(url: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return request<T>(url, { method: 'DELETE', body, signal });
}
